import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserPlus, Trash2, Check, Eye, ShieldCheck, Clock, X } from "lucide-react";

const PERMISSIONS = [
  { v: "view_only", labelKey: "perm_view_only" },
  { v: "full", labelKey: "perm_full" },
  { v: "claims_only", labelKey: "perm_claims_only" },
];
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");

export default function Household() {
  const { t } = useI18n();
  const permLabel = (v) => t(PERMISSIONS.find((p) => p.v === v)?.labelKey || v);
  const [members, setMembers] = useState([]);
  const [shared, setShared] = useState([]);
  const [email, setEmail] = useState("");
  const [perm, setPerm] = useState("view_only");
  const [viewing, setViewing] = useState(null);

  const load = () => {
    api.get("/household/members").then((r) => setMembers(r.data));
    api.get("/household/shared-with-me").then((r) => setShared(r.data));
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email) { toast.error(t("enter_email")); return; }
    try { await api.post("/household/invite", { email, permission: perm }); toast.success(t("invitation_added")); setEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); }
  };
  const revoke = async (id) => { await api.delete(`/household/members/${id}`); load(); };
  const changePerm = async (id, v) => { await api.put(`/household/members/${id}`, { permission: v }); load(); };
  const accept = async (id) => { await api.post(`/household/accept/${id}`); toast.success(t("access_accepted")); load(); };
  const viewWallet = async (ownerId) => {
    try { const r = await api.get(`/household/${ownerId}/summary`); setViewing(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No access"); }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Users className="h-7 w-7 text-brandblue" /> {t("household_title")}</h1>
      <p className="mt-1 text-slate-500">{t("household_sub")}</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* People I've invited */}
        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy">{t("people_with_access")}</h2>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input data-testid="household-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="family@email.com" className="rounded-xl" />
            <Select value={perm} onValueChange={setPerm}>
              <SelectTrigger data-testid="household-permission" className="rounded-xl sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{PERMISSIONS.map((p) => <SelectItem key={p.v} value={p.v}>{t(p.labelKey)}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="household-invite-btn" onClick={invite} className="rounded-full brand-gradient text-white"><UserPlus className="h-4 w-4 mr-1" />{t("invite")}</Button>
          </div>
          <div className="mt-4 space-y-3">
            {members.length === 0 && <p className="text-sm text-slate-400">{t("no_shared_yet")}</p>}
            {members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full brand-gradient text-white text-sm font-semibold">{(m.member_name || "?")[0].toUpperCase()}</span>
                <div className="flex-1 min-w-0"><p className="font-medium text-navy truncate">{m.member_name}</p><p className="text-xs text-slate-400 truncate">{m.member_email}</p></div>
                {m.status === "pending"
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-600"><Clock className="h-3 w-3" />{t("pending")}</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs text-emerald-600"><Check className="h-3 w-3" />{t("active")}</span>}
                <Select value={m.permission} onValueChange={(v) => changePerm(m.id, v)}>
                  <SelectTrigger className="w-32 rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERMISSIONS.map((p) => <SelectItem key={p.v} value={p.v}>{t(p.labelKey)}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => revoke(m.id)} className="rounded-full p-2 text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Shared with me */}
        <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <h2 className="text-lg font-semibold text-navy">{t("shared_with_me")}</h2>
          <div className="mt-4 space-y-3">
            {shared.length === 0 && <p className="text-sm text-slate-400">{t("no_one_shared")}</p>}
            {shared.map((s) => (
              <div key={s.id} data-testid={`shared-${s.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-50 text-brandteal"><ShieldCheck className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0"><p className="font-medium text-navy truncate">{t("persons_insurance", { name: s.owner_name })}</p><p className="text-xs text-slate-400">{permLabel(s.permission)}</p></div>
                {s.status === "pending"
                  ? <Button data-testid={`accept-${s.id}`} onClick={() => accept(s.id)} className="rounded-full brand-gradient text-white h-9"><Check className="h-4 w-4 mr-1" />{t("accept")}</Button>
                  : (s.permission !== "claims_only" && <Button data-testid={`view-${s.id}`} variant="outline" onClick={() => viewWallet(s.owner_id)} className="rounded-full h-9"><Eye className="h-4 w-4 mr-1" />{t("view")}</Button>)}
              </div>
            ))}
          </div>

          {viewing && (
            <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid="family-summary">
              <div className="flex items-center justify-between">
                <p className="font-medium text-navy">{t("activity_summary")} · {viewing.owner.name}</p>
                <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-navy"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-white p-3 text-center"><p className="text-2xl font-semibold text-navy">{viewing.protection_score ?? "—"}</p><p className="text-[11px] text-slate-400">{t("protection_score")}</p></div>
                <div className="rounded-xl bg-white p-3 text-center"><p className="text-2xl font-semibold text-navy">{viewing.policy_count}</p><p className="text-[11px] text-slate-400">{t("policies")}</p></div>
                <div className="rounded-xl bg-white p-3 text-center"><p className="text-2xl font-semibold text-navy">{viewing.total_conversations}</p><p className="text-[11px] text-slate-400">{t("ai_chats")}</p></div>
              </div>
              {viewing.upcoming_reminders?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("upcoming_renewals")}</p>
                  <div className="mt-2 space-y-1.5">
                    {viewing.upcoming_reminders.map((r, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
                        <span className="text-navy">{r.title}</span>
                        <span className="text-brandblue font-medium">{r.due_date ? new Date(r.due_date).toLocaleDateString() : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 space-y-2">
                {viewing.policies.map((p, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl bg-white p-3 text-sm">
                    <span className="font-medium capitalize text-navy">{p.category} · {p.carrier || "—"}</span>
                    <span className="text-slate-500">{money(p.annual_premium)}/yr</span>
                  </div>
                ))}
                {viewing.policies.length === 0 && <p className="text-sm text-slate-400">{t("no_policies_show")}</p>}
              </div>
              <p className="mt-3 text-xs text-slate-400">{t("view_only_note")}</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
